import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '../Header/Header';
import Sidebar from '../Sidebar/Sidebar';
import styles from './MainLayout.module.css';
import { isTelegramMiniApp } from '../../utils/telegram';

const MainLayout = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const mainContentRef = useRef(null);
  const hideSidebar = location.pathname.startsWith('/profile');
  const isTelegramMiniAppDetected = typeof window !== 'undefined' && isTelegramMiniApp();
  const isMiniAppApprovals = isTelegramMiniAppDetected && location.pathname.startsWith('/approvals');

  useEffect(() => {
    const handleResize = () => {
      const mobileView = window.innerWidth <= 992;
      setIsMobile(mobileView);

      if (!mobileView) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let rafId = null;

    const applyResponsiveLabels = () => {
      const root = mainContentRef.current;
      if (!root) return;

      const tables = Array.from(root.querySelectorAll('table'));
      tables.forEach((table) => {
        const headCells = table.querySelectorAll('thead th');
        if (!headCells.length) return;
        const labels = Array.from(headCells).map((cell) => cell.textContent?.trim() || '');
        table.querySelectorAll('tbody tr').forEach((row) => {
          Array.from(row.cells).forEach((cell, index) => {
            if (cell.colSpan > 1) {
              cell.removeAttribute('data-label');
              return;
            }
            const label = labels[index];
            if (label) {
              cell.setAttribute('data-label', label);
            }
          });
        });
      });
    };

    const scheduleApplyResponsiveLabels = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        applyResponsiveLabels();
      });
    };

    const observer = new MutationObserver(scheduleApplyResponsiveLabels);
    if (mainContentRef.current) {
      observer.observe(mainContentRef.current, { childList: true, subtree: true });
    }
    window.addEventListener('resize', scheduleApplyResponsiveLabels);
    scheduleApplyResponsiveLabels();

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      observer.disconnect();
      window.removeEventListener('resize', scheduleApplyResponsiveLabels);
    };
  }, []);

  const mainClassNames = [styles.main];
  if (isMobile) {
    mainClassNames.push(styles.mobileMain);
  }
  if (hideSidebar) {
    mainClassNames.push(styles.fullWidth);
  }

  const handleToggleSidebar = () => {
    if (!isMobile) return;
    setIsSidebarOpen(prev => !prev);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  if (isMiniAppApprovals) {
    return (
      <div className={`${styles.layout} ${styles.miniAppLayout}`}>
        <main className={`${styles.main} ${styles.miniAppMain}`}>
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <Header
        isMobile={isMobile}
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={handleToggleSidebar}
      />
      <div className={styles.content}>
        {!hideSidebar && (
          <Sidebar
            isMobile={isMobile}
            isOpen={isMobile ? isSidebarOpen : true}
            onClose={handleCloseSidebar}
          />
        )}
        <main ref={mainContentRef} className={mainClassNames.join(' ')}>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
